import { PAYMENT_MODES } from "constants/paymentModes";
import { VERNACULAR_DEFAULT_MESSAGES, VERNACULAR_STRING_IDS } from "constants/vernacularConstants";
import { getIcons, ICONS_NAMES } from "helpers/utils/getIcons";
import { getStringFromId } from "helpers/vernacular";
import { getPaymentAlert } from "../../../../../logics/src/utils/getPaymentAlert";
import { BUTTON_TYPE } from "constants/index";
import { getPlatform } from "../../../../../helpers/index";
import { IOfferMessage, ISavedUpiIntent, OfferDataInterface } from "../@types/savedMethods";
import { doCheckOffers } from "logics/src/utils/checkOffers";
import { getOfferMessages } from "logics/src/utils/helpers";
import { merchantKeys } from "../../constants";
import { getUpiIntentListFromRemoteConfig } from "layouts/CheckoutPayment/helpers";

/**
 * function to check if upiIntent visible or not.
 */
const checkIsUpiIntentVisibleOnSavedPayment = ({
  isApp,
  lastPaymentInfo,
  isAllUpiIntentAvailableOnWeb,
  upiIntentDetails,
  installedPSP,
}: any) => {
  /**
   * for mweb if  allupiIntent exp is disable 
   * then show only google pay as upi intent option.
   */
  if (!isApp) {
    if (
      !lastPaymentInfo?.packageName ||
      (!isAllUpiIntentAvailableOnWeb && upiIntentDetails?.title !== "Google Pay")
    ) {
      return false;
    }
  }

  /**
   * for mweb if allupiIntent exp is enable 
   * then show phonepe, paytm, google pay acc to lastpayment info.
   */
  if (!isApp && isAllUpiIntentAvailableOnWeb && !installedPSP.includes(upiIntentDetails?.title)) {
    return false;
  }

  /**
   * for apps show that upi-intent option which received in callback from apps
   */
  if (isApp && (!installedPSP.includes(upiIntentDetails?.title) || !lastPaymentInfo?.packageName)) {
    return false;
  }

  return true;
};

export const getUpiIntentContextData = ({  
    paymentContext,  
    orderAmount: grandTotal,
    doPayment,
    strikedPaymentText,
    isApp,
    timerCallback,
    toggleOfferBottomSheet,
    theme,
    isAllUpiIntentAvailableOnWeb,
    handleMwebIntentPollingScreen
    }: any) => {
    const platform = getPlatform();
    let upiIntentList = paymentContext?.upiIntentList;

    if(!isApp && isAllUpiIntentAvailableOnWeb){
      upiIntentList = getUpiIntentListFromRemoteConfig();
    }

    const installedPSP = upiIntentList?.map((intent: any) => intent?.title);

    let title = getStringFromId(
        VERNACULAR_STRING_IDS.GOOGLE_PAY_TITLE,
        VERNACULAR_DEFAULT_MESSAGES.GOOGLE_PAY_TITLE
      );
    let subTitle = getStringFromId(
      VERNACULAR_STRING_IDS.GOOGLE_PAY_SUBTITLE,
      VERNACULAR_DEFAULT_MESSAGES.GOOGLE_PAY_SUBTITLE
    );

    
    const labelText = getStringFromId(
      VERNACULAR_STRING_IDS.MOBILE_NUMBER_OR_GPAY_ID,
      VERNACULAR_DEFAULT_MESSAGES.MOBILE_NUMBER_OR_GPAY_ID
    );
    const payNowText = getStringFromId(
      VERNACULAR_STRING_IDS.DYNAMIC_PAY,
      `Pay ₹${grandTotal}`,
      { count: grandTotal }
    );

    const paymentInfo = paymentContext?.paymentInfo;
    const paymentAlertInfo = paymentInfo?.paymentAlertInfo; 
    const alertConfig = getPaymentAlert(paymentAlertInfo, PAYMENT_MODES.UPI, paymentInfo);
    const lastPaymentInfo = paymentInfo?.lastPaymentInfo;
    const upiMapping = paymentInfo?.upiMapping;
    const upiIntentDetails = upiMapping.find((upi: any) => upi?.package === lastPaymentInfo?.packageName);
    const cartRules = paymentContext?.offersData;
    
    const isShowingUpiIntentOnsavedPaymentOption = checkIsUpiIntentVisibleOnSavedPayment({
      isApp,
      lastPaymentInfo,
      isAllUpiIntentAvailableOnWeb,
      upiIntentDetails,
      installedPSP
    });

    if(!isShowingUpiIntentOnsavedPaymentOption){
        return {} as ISavedUpiIntent;
    }

    if (isApp || isAllUpiIntentAvailableOnWeb) {
      title = getStringFromId(VERNACULAR_STRING_IDS.UPI, VERNACULAR_DEFAULT_MESSAGES.UPI);
      subTitle = upiIntentDetails?.title;
    }

    const offerKeys = lastPaymentInfo?.offerKeys || [];
    let offerMessages: IOfferMessage[] = [];
    let revisedBtnText = strikedPaymentText;
    let offerData = {} as OfferDataInterface;
    let offers: any[] = [];

    if (offerKeys?.length) {
      offerKeys.forEach((offerKey: string) => {
        let offer = cartRules.paymentRules.filter(
          (rule: PaymentOfferRule) => rule.ruleKey === offerKey
        );
        offers.push(offer);
        offers = offers.flat();
      });
      if (offers?.length > 1) {
        offers = offers.sort(
          (a: PaymentOfferRule, b: PaymentOfferRule) => b?.priority - a?.priority
        );
      }

      /**
       * finding the payment offer for upi intent by method: upi. 
       * rather than fetching from method: upi_intent.
       */
      if (offers?.length) {
        const keys = offers.map(offer => offer?.ruleKey);
        offerData = doCheckOffers(false, PAYMENT_MODES.UPI, cartRules, paymentInfo, keys);
        if (offerData?.hasOffers && offerData?.finalOrderAmount) {
          offerMessages = getOfferMessages(offerData, theme, () =>
            toggleOfferBottomSheet(offerData)
          );

          revisedBtnText = getStringFromId(
            VERNACULAR_STRING_IDS.DYNAMIC_PAY_WITH_STRIKED_AMOUNT,
            `Pay ₹${offerData?.finalOrderAmount} &nbsp; <del> ₹${grandTotal}</del>`,
            {
              oldAmount: grandTotal,
              newAmount: offerData?.finalOrderAmount || "",
            }
          );
        }
      }
    }

    const config = {
      inputVisible: false,
      title,
      subTitle,
      labelText,
      payNowText,
      revisedPayNowText: revisedBtnText,
      errorText: '',
      offerText: '',
      alert: {
        leadingIcon: getIcons(platform, ICONS_NAMES.WARN_ICON),
        warn: true,
        text: '',
      },
      phoneNumber: '',
      leadingIcon: upiIntentDetails?.iconIntentUrl,
      onChange: () => {},
      doPayment: () => {
        doPayment(PAYMENT_MODES.INTENT, {
          packageName: upiIntentDetails?.package,
          isSavedUpi: true,
          timerCallback,
          grandTotal,
          offerData,
          isSaved:true,
          merchantKey: merchantKeys[upiIntentDetails?.title],
          handleMwebIntentPollingScreen,
          intentApp: merchantKeys[upiIntentDetails?.title]
        });
      },
      onBlur: () => { },
      isIntentFlow: true,
      pymentMethod: PAYMENT_MODES.INTENT,
      priority: !alertConfig?.isPaymentEnabled ? 1 : 0,
      theme,
      buttonType: BUTTON_TYPE.MEDIUM,
      offerMessages
    } as ISavedUpiIntent;

    return config;

}